<template>
  <div v-if="showPreview" class="rule-preview-panel">
    <div class="preview-header">
      <i class="pi pi-chart-line"></i>
      <h3>{{ t('Prévia de Impacto') }}</h3>
    </div>

    <!-- Employee Info -->
    <div class="preview-section">
      <div class="section-title">{{ t('Funcionário') }}</div>
      <div class="employee-info">
        <strong>{{ employeeName }}</strong>
      </div>
    </div>

    <!-- Last Month's Actuals (if available) -->
    <div v-if="lastMonthData" class="preview-section">
      <div class="section-title">{{ t('Mês Anterior') }}</div>
      <div class="actuals-grid">
        <div class="actual-item">
          <span class="actual-label">{{ t('Receita:') }}</span>
          <span class="actual-value">{{ n(lastMonthData.revenue || 0, 'currency') }}</span>
        </div>
        <div class="actual-item">
          <span class="actual-label">{{ t('Pago Atual:') }}</span>
          <span class="actual-value">{{ n(lastMonthData.totalPaid || 0, 'currency') }}</span>
        </div>
      </div>
    </div>

    <!-- Existing Rules -->
    <div v-if="existingRules.length > 0" class="preview-section">
      <div class="section-title">
        {{ t('Regras Existentes:') }} {{ existingRules.length }}
      </div>
      <div class="rules-list">
        <div v-for="rule in existingRules" :key="rule.id" class="rule-preview-item">
          <div class="rule-type-badge">{{ getRuleTypeLabel(rule.rule_type) }}</div>
          <div class="rule-amount">{{ formatRuleAmount(rule) }}</div>
        </div>
      </div>
      <div class="existing-total">
        <strong>{{ t('Total Existente:') }}</strong>
        <span class="total-amount">{{ formatExistingTotal() }}</span>
      </div>
    </div>

    <!-- New Rule Preview -->
    <div v-if="wizardData.rule_type" class="preview-section highlighted">
      <div class="section-title">{{ t('Nova Regra') }}</div>
      <div class="new-rule-preview">
        <div class="rule-type-badge">{{ getRuleTypeLabel(wizardData.rule_type) }}</div>
        <div class="rule-details">
          <div v-if="wizardData.rule_type === 'fixed'">
            <span class="rule-amount">{{ n(wizardData.amount || 0, 'currency') }}</span>
            <small class="rule-period">{{ getPeriodTypeLabel() }}</small>
          </div>
          <div v-else-if="isCommissionRule">
            <span v-if="wizardData.commission_structure === 'simple'" class="rule-amount">
              {{ n((wizardData.amount || 0) * 100, 'percent') }}
            </span>
            <span v-else class="rule-amount">
              {{ getCommissionStructureLabel() }}
            </span>
            <div v-if="sampleCalculation !== null" class="sample-calc">
              <small>{{ t('Exemplo (R$45.000):') }}</small>
              <strong>{{ n(sampleCalculation, 'currency') }}</strong>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Impact Summary -->
    <div v-if="wizardData.rule_type" class="preview-section impact">
      <div class="section-title">{{ t('Impacto Total') }}</div>
      <div class="impact-summary">
        <div class="impact-row">
          <span>{{ t('Com nova regra:') }}</span>
          <strong class="impact-amount">{{ formatTotalWithNewRule() }}</strong>
        </div>
        <div v-if="lastMonthData" class="impact-change">
          <span>{{ t('Variação:') }}</span>
          <strong
            :class="{
              'change-positive': totalChange > 0,
              'change-negative': totalChange < 0,
              'change-neutral': totalChange === 0
            }"
          >
            {{ formatChange() }}
          </strong>
        </div>
      </div>

      <!-- Warning if significant change -->
      <div v-if="Math.abs(totalChange) > 0.3" class="impact-warning">
        <i class="pi pi-exclamation-triangle"></i>
        <span v-if="totalChange > 0">
          {{ t('Aumento de') }} {{ n(Math.abs(totalChange) * 100, 'percent') }} {{ t('no pagamento!') }}
        </span>
        <span v-else>
          {{ t('Redução de') }} {{ n(Math.abs(totalChange) * 100, 'percent') }} {{ t('no pagamento!') }}
        </span>
      </div>
    </div>

    <!-- Info Footer -->
    <div class="preview-footer">
      <i class="pi pi-info-circle"></i>
      <span>{{ t('Valores baseados no mês anterior se disponíveis') }}</span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useI18n as useI18nFormatter } from 'vue-i18n';

type Id = number | string;

interface Bracket {
  min: number;
  max: number | null;
  /** Rate stored as fraction (0..1). PRESERVED. */
  rate: number;
}

interface WizardData {
  franchise_id?: Id;
  user_id?: Id;
  rule_type?: string;
  commission_structure?: 'simple' | 'tiered' | 'progressive';
  /** Amount: currency for fixed, fraction (0..1) for commission. PRESERVED. */
  amount?: number;
  brackets?: Bracket[];
  period_type?: string;
  description?: string;
  meta?: string | null;
}

interface ExistingPaymentRule {
  id: Id;
  rule_type: string;
  amount: number | string;
  [key: string]: unknown;
}

interface LastMonthData {
  revenue: number;
  totalPaid: number;
}

const props = withDefaults(defineProps<{
  wizardData: WizardData;
  existingRules?: ExistingPaymentRule[];
  isEditMode?: boolean;
}>(), {
  existingRules: () => [],
  isEditMode: false
});

const { t } = useI18n();
const { n } = useI18nFormatter();

const showPreview = computed<boolean>(() => {
  return !!(props.wizardData.user_id && props.wizardData.franchise_id);
});

const employeeName = computed<string>(() => {
  // This would be populated from the employee data
  return props.wizardData.user_id ? 'Funcionário Selecionado' : '';
});

const lastMonthData = computed<LastMonthData | null>(() => {
  // This would be fetched from actual payroll data
  // For now, return mock data if we have an employee
  // PRESERVED mock values: revenue=45000, totalPaid=4250
  if (props.wizardData.user_id) {
    return {
      revenue: 45000,
      totalPaid: 4250
    };
  }
  return null;
});

const isCommissionRule = computed<boolean>(() => {
  return !!props.wizardData.rule_type && props.wizardData.rule_type.includes('commission');
});

const sampleCalculation = computed<number | null>(() => {
  if (!isCommissionRule.value) return null;

  // PRESERVED sample revenue: 45000
  const sampleRevenue = 45000;

  if (props.wizardData.commission_structure === 'simple') {
    return sampleRevenue * (props.wizardData.amount || 0);
  }

  if (props.wizardData.brackets && props.wizardData.brackets.length > 0) {
    // For tiered/progressive, calculate based on first bracket matching
    const bracket = props.wizardData.brackets.find(b =>
      sampleRevenue >= b.min && (b.max === null || sampleRevenue <= b.max)
    );
    if (bracket) {
      return sampleRevenue * bracket.rate;
    }
  }

  return null;
});

const existingTotal = computed<number>(() => {
  return props.existingRules.reduce<number>((sum, rule) => {
    if (rule.rule_type === 'fixed') {
      return sum + parseFloat(String(rule.amount));
    }
    // For commission, use a sample calculation against last-month revenue
    // PRESERVED: rate is fraction (rule.amount), multiplied by revenue.
    if (lastMonthData.value) {
      const rate = parseFloat(String(rule.amount)) || 0;
      return sum + (lastMonthData.value.revenue * rate);
    }
    return sum;
  }, 0);
});

const newRuleAmount = computed<number>(() => {
  if (!props.wizardData.rule_type) return 0;

  if (props.wizardData.rule_type === 'fixed') {
    return props.wizardData.amount || 0;
  }

  if (isCommissionRule.value) {
    if (props.wizardData.commission_structure === 'simple') {
      if (lastMonthData.value) {
        return (lastMonthData.value.revenue || 0) * (props.wizardData.amount || 0);
      }
    }
    return sampleCalculation.value || 0;
  }

  return 0;
});

const totalWithNewRule = computed<number>(() => {
  return existingTotal.value + newRuleAmount.value;
});

const totalChange = computed<number>(() => {
  if (!lastMonthData.value) return 0;
  const current = lastMonthData.value.totalPaid || 0;
  if (current === 0) return 0;
  return (totalWithNewRule.value - current) / current;
});

const getRuleTypeLabel = (ruleType: string | undefined): string => {
  const labels: Record<string, string> = {
    'fixed': t('Salário Fixo'),
    'commission_over_gross_revenue_executed': t('Comissão Executada'),
    'commission_over_gross_revenue_sold_remotely': t('Comissão Remota'),
    'commission_over_gross_revenue_sold_in_loco': t('Comissão Presencial')
  };
  return (ruleType && labels[ruleType]) || ruleType || '';
};

const getPeriodTypeLabel = (): string => {
  const labels: Record<string, string> = {
    'weekly': t('Semanal'),
    'biweekly': t('Quinzenal'),
    'monthly': t('Mensal')
  };
  const periodType = props.wizardData.period_type;
  return (periodType && labels[periodType]) || periodType || '';
};

const getCommissionStructureLabel = (): string => {
  const labels: Record<string, string> = {
    'simple': t('Simples'),
    'tiered': t('Escalonada'),
    'progressive': t('Progressiva')
  };
  const cs = props.wizardData.commission_structure;
  return (cs && labels[cs]) || cs || '';
};

const formatRuleAmount = (rule: ExistingPaymentRule): string => {
  // PRESERVED: amount is fraction for commission, currency for fixed
  if (rule.rule_type === 'fixed') {
    return n(parseFloat(String(rule.amount)), 'currency');
  }
  return `${Math.round(parseFloat(String(rule.amount)) * 100)}%`;
};

const formatExistingTotal = (): string => {
  // Check if any commission rules exist
  const hasCommission = props.existingRules.some(r => r.rule_type.includes('commission'));

  if (hasCommission) {
    return t('Depende da receita');
  }

  return n(existingTotal.value, 'currency');
};

const formatTotalWithNewRule = (): string => {
  const hasCommission = props.existingRules.some(r => r.rule_type.includes('commission')) ||
                       isCommissionRule.value;

  if (hasCommission) {
    return t('Depende da receita');
  }

  return n(totalWithNewRule.value, 'currency');
};

const formatChange = (): string => {
  if (totalChange.value === 0) return t('Sem mudança');

  const absChange = Math.abs(totalChange.value);
  const sign = totalChange.value > 0 ? '+' : '';
  return `${sign}${n(absChange * 100, 'percent')}`;
};
</script>

<style scoped>
.rule-preview-panel {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 1.25rem;
  background: var(--surface-50);
  border: 1px solid var(--surface-border);
  border-radius: 8px;
}

.preview-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid var(--surface-border);
}

.preview-header i {
  font-size: 1.25rem;
  color: var(--primary-color);
}

.preview-header h3 {
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  color: var(--text-color);
}

.preview-section {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.section-title {
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--text-color-secondary);
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.employee-info strong {
  font-size: 1rem;
  color: var(--text-color);
}

.actuals-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.75rem;
}

.actual-item {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.actual-label {
  font-size: 0.75rem;
  color: var(--text-color-secondary);
}

.actual-value {
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--text-color);
}

.rules-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.rule-preview-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 0.75rem;
  background: var(--surface-0);
  border-radius: 4px;
  font-size: 0.875rem;
}

.rule-type-badge {
  padding: 0.25rem 0.5rem;
  background: var(--primary-color);
  color: white;
  border-radius: 4px;
  font-size: 0.7rem;
  font-weight: 600;
}

.rule-amount {
  font-weight: 600;
  color: var(--text-color);
}

.existing-total {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 0.5rem;
  border-top: 1px solid var(--surface-border);
  font-size: 0.9rem;
}

.total-amount {
  font-weight: 700;
  color: var(--text-color);
}

.preview-section.highlighted {
  padding: 1rem;
  background: var(--primary-50);
  border: 2px solid var(--primary-color);
  border-radius: 6px;
}

.new-rule-preview {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.rule-details {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.rule-amount {
  font-size: 1.25rem;
  font-weight: 700;
  color: var(--primary-color);
}

.rule-period {
  font-size: 0.875rem;
  color: var(--text-color-secondary);
}

.sample-calc {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  padding: 0.5rem;
  background: var(--surface-0);
  border-radius: 4px;
}

.sample-calc small {
  font-size: 0.75rem;
  color: var(--text-color-secondary);
}

.sample-calc strong {
  font-size: 0.9rem;
  color: var(--text-color);
}

.preview-section.impact {
  padding: 1rem;
  background: var(--surface-0);
  border: 2px solid var(--surface-border);
  border-radius: 6px;
}

.impact-summary {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.impact-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 0.9rem;
}

.impact-amount {
  font-size: 1.125rem;
  font-weight: 700;
  color: var(--text-color);
}

.impact-change {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 0.5rem;
  border-top: 1px dashed var(--surface-border);
  font-size: 0.875rem;
}

.change-positive {
  color: var(--green-600);
}

.change-negative {
  color: var(--red-600);
}

.change-neutral {
  color: var(--text-color-secondary);
}

.impact-warning {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem;
  background: var(--orange-50);
  border-left: 3px solid var(--orange-500);
  border-radius: 4px;
  font-size: 0.875rem;
  color: var(--orange-900);
}

.impact-warning i {
  color: var(--orange-500);
}

.preview-footer {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding-top: 0.75rem;
  border-top: 1px solid var(--surface-border);
  font-size: 0.8rem;
  color: var(--text-color-secondary);
}

.preview-footer i {
  color: var(--primary-color);
}

@media (max-width: 768px) {
  .actuals-grid {
    grid-template-columns: 1fr;
  }

  .impact-row,
  .impact-change {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.25rem;
  }
}
</style>
